import {
  type CustomerStatus,
  type DeliveryStatus,
  type OrderStatus,
  type Paginated,
  type ProductStatus,
  type StockAdjustment,
  type StockAdjustmentListQuery,
} from "@milqit/shared-types";

import { apiFetch, qs } from "./client";

/* Sidebar badges ---------------------------------------------------------- */

export interface NavCounts {
  newOrders: number;
  lowStock: number;
  unassigned: number;
  openTickets: number;
  pendingRefunds?: number;
  liveOrders?: number;
}

export async function getNavCounts(): Promise<NavCounts> {
  return apiFetch("/admin/nav-counts");
}

/* Revenue series ---------------------------------------------------------- */

export interface RevenuePoint {
  date: string;
  plan: number;
  oneTime: number;
  orders: number;
}

/** Delivered revenue per morning, split into milk-plan and one-time orders. */
export async function getRevenueSeries(days = 30): Promise<RevenuePoint[]> {
  return apiFetch(`/admin/revenue?days=${days}`);
}

/* Top products ------------------------------------------------------------ */

export interface TopProduct {
  productId: string;
  name: string;
  image: string;
  units: number;
  revenue: number;
}

export async function getTopProducts(limit = 5): Promise<TopProduct[]> {
  return apiFetch(`/admin/top-products?limit=${limit}`);
}

/* Stock history ----------------------------------------------------------- */

export async function listStockAdjustments(
  productId: string,
  query: StockAdjustmentListQuery,
): Promise<Paginated<StockAdjustment>> {
  return apiFetch(`/admin/inventory/${productId}/adjustments${qs({ ...query })}`);
}

/* Bulk actions ------------------------------------------------------------ */

export async function setProductsStatus(ids: string[], status: ProductStatus): Promise<number> {
  return apiFetch("/admin/products/bulk-status", {
    method: "POST",
    body: JSON.stringify({ ids, status }),
  });
}

export async function deleteProducts(ids: string[]): Promise<{ deleted: number; skipped: number }> {
  return apiFetch("/admin/products/bulk-delete", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });
}

/** Moves each selected order to `status` when that transition is allowed; others are skipped. */
export async function advanceOrders(
  ids: string[],
  status: OrderStatus,
): Promise<{ updated: number; skipped: number }> {
  return apiFetch("/admin/orders/bulk-status", {
    method: "POST",
    body: JSON.stringify({ ids, status }),
  });
}

export async function assignDeliveries(
  ids: string[],
  agentId: string,
  status?: DeliveryStatus,
): Promise<number> {
  return apiFetch("/admin/deliveries/bulk-assign", {
    method: "POST",
    body: JSON.stringify({ ids, agentId, status }),
  });
}

export async function setCustomersStatus(ids: string[], status: CustomerStatus): Promise<number> {
  return apiFetch("/admin/customers/bulk-status", {
    method: "POST",
    body: JSON.stringify({ ids, status }),
  });
}
